import fs from "node:fs/promises";
import path from "node:path";
import { resolveSecret, writeConfig, type NoriConfig } from "@mewbleh/nori-core";
import type { NoriPaths } from "@mewbleh/nori-persistence";

export type SecurityFinding = {
  checkId: string;
  severity: "info" | "warning" | "critical";
  title: string;
  detail: string;
  remediation?: string;
};

export type SecurityReport = {
  summary: { critical: number; warning: number; info: number };
  findings: SecurityFinding[];
  checkedAt: number;
};

async function modeFinding(filePath: string, expected: number, checkId: string): Promise<SecurityFinding | undefined> {
  if (process.platform === "win32") return undefined;
  try {
    const stat = await fs.stat(filePath);
    const actual = stat.mode & 0o777;
    if ((actual & ~expected) === 0) return undefined;
    return {
      checkId,
      severity: stat.isDirectory() ? "warning" : "critical",
      title: "Sensitive path permissions are too broad",
      detail: `${filePath} has mode ${actual.toString(8)}; expected ${expected.toString(8)} or stricter.`,
      remediation: "Run `nori security audit --fix`.",
    };
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") return undefined;
    return { checkId, severity: "warning", title: "Could not inspect path permissions", detail: `${filePath}: ${(error as Error).message}` };
  }
}

export async function auditSecurity(paths: NoriPaths, config: NoriConfig): Promise<SecurityReport> {
  const findings: SecurityFinding[] = [];
  const loopback = config.gateway.bind === "127.0.0.1" || config.gateway.bind === "::1" || config.gateway.bind === "localhost";
  if (!loopback && !config.gateway.auth.token && !process.env.NORI_GATEWAY_TOKEN && !process.env.OPENCLAW_GATEWAY_TOKEN) {
    findings.push({
      checkId: "gateway.non_loopback_without_auth",
      severity: "critical",
      title: "Non-loopback Gateway has no configured token",
      detail: `Gateway is bound to ${config.gateway.bind} without an explicit authentication source.`,
      remediation: "Configure gateway.auth.token using an env/file SecretRef.",
    });
  } else if (!loopback) {
    findings.push({
      checkId: "gateway.non_loopback_exposure",
      severity: "warning",
      title: "Gateway is reachable beyond loopback",
      detail: `Gateway bind is ${config.gateway.bind}; put TLS and a trusted network boundary in front of remote access.`,
    });
  }
  if (config.session.dmScope === "main") {
    findings.push({
      checkId: "session.shared_dm_scope",
      severity: "warning",
      title: "DM senders share one session",
      detail: "session.dmScope=main can mix context from different senders.",
      remediation: "Use per-channel-peer or per-account-channel-peer.",
    });
  }
  for (const [channelId, channel] of Object.entries(config.channels)) {
    const accountMap = "accounts" in channel && channel.accounts && typeof channel.accounts === "object"
      ? channel.accounts
      : { default: channel };
    for (const [accountId, account] of Object.entries(accountMap)) {
      if (account.dmPolicy === "open" || account.groupPolicy === "open") {
        findings.push({
          checkId: "channels.open_ingress",
          severity: config.tools.shell || config.tools.filesystem || config.tools.browser ? "critical" : "warning",
          title: "Open channel ingress is enabled",
          detail: `${channelId}/${accountId} accepts ${account.dmPolicy === "open" ? "open DMs" : "open groups"}${config.tools.shell || config.tools.filesystem || config.tools.browser ? " while consequential tools are enabled" : ""}.`,
          remediation: "Use pairing/allowlists and keep consequential tools approval-gated.",
        });
      }
    }
  }
  for (const [providerId, provider] of Object.entries(config.providers)) {
    if (!provider.baseUrl) continue;
    try {
      const url = new URL(provider.baseUrl);
      const loopback = url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "::1" || url.hostname.endsWith(".localhost");
      if (url.protocol === "http:" && !loopback) findings.push({
        checkId: "providers.insecure_endpoint",
        severity: provider.apiKey ? "critical" : "warning",
        title: "Provider endpoint uses plaintext HTTP",
        detail: `${providerId} sends requests to ${url.origin}${provider.apiKey ? " with configured authentication" : ""}.`,
        remediation: "Use HTTPS or a loopback-only local provider endpoint.",
      });
    } catch { /* configuration validation reports malformed URLs */ }
  }
  for (const [name, server] of Object.entries(config.mcp.servers)) {
    if (server.enabled === false || (server.transport ?? (server.url ? "streamable-http" : "stdio")) !== "stdio") continue;
    findings.push({
      checkId: "mcp.trusted_process",
      severity: "warning",
      title: "Enabled stdio MCP server runs as trusted local code",
      detail: `${name} starts ${server.command ?? "a configured command"} with the Nori operating-system account. Tool calls remain approval-gated, but process startup itself is trusted.`,
    });
  }
  const enabledTools = (["filesystem", "shell", "browser", "automation", "externalMessaging"] as const).filter((key) => config.tools[key]);
  if (enabledTools.length) {
    findings.push({
      checkId: "tools.consequential_enabled",
      severity: "warning",
      title: "Consequential tools are enabled",
      detail: `Enabled: ${enabledTools.join(", ")}. Nori still requires durable approvals where implemented.`,
    });
  }
  if (config.plugins.allow.length) {
    findings.push({
      checkId: "plugins.third_party_enabled",
      severity: "warning",
      title: "Explicitly allowed plugins execute as trusted in-process code",
      detail: `Allowed plugin ids: ${config.plugins.allow.join(", ")}. Only allow packages you trust with the Nori operating-system account.`,
    });
  }
  const permissionChecks = await Promise.all([
    modeFinding(paths.stateDir, 0o700, "filesystem.state_permissions"),
    modeFinding(paths.credentialsDir, 0o700, "filesystem.credentials_permissions"),
    modeFinding(paths.configPath, 0o600, "filesystem.config_permissions"),
    modeFinding(paths.masterKeyPath, 0o600, "filesystem.master_key_permissions"),
  ]);
  findings.push(...permissionChecks.filter((entry): entry is SecurityFinding => Boolean(entry)));
  const secrets = [
    ["gateway.auth.token", config.gateway.auth.token],
    ...Object.entries(config.providers).map(([id, provider]) => [`providers.${id}.apiKey`, provider.apiKey] as const),
  ] as const;
  for (const [label, reference] of secrets) {
    if (!reference) continue;
    try {
      if (!(await resolveSecret(reference))) findings.push({ checkId: "secrets.unavailable", severity: "warning", title: "Secret reference is empty", detail: label });
    } catch (error) {
      findings.push({ checkId: "secrets.unavailable", severity: "warning", title: "Secret reference is unavailable", detail: `${label}: ${(error as Error).message}` });
    }
  }
  return {
    summary: {
      critical: findings.filter((finding) => finding.severity === "critical").length,
      warning: findings.filter((finding) => finding.severity === "warning").length,
      info: findings.filter((finding) => finding.severity === "info").length,
    },
    findings,
    checkedAt: Date.now(),
  };
}

export async function fixSecurity(paths: NoriPaths, config: NoriConfig): Promise<{ changed: string[] }> {
  const changed: string[] = [];
  for (const [channelId, channel] of Object.entries(config.channels)) {
    const accountMap = "accounts" in channel && channel.accounts && typeof channel.accounts === "object"
      ? channel.accounts
      : { default: channel };
    for (const [accountId, account] of Object.entries(accountMap)) {
      if (account.groupPolicy === "open") {
        account.groupPolicy = "allowlist";
        changed.push(`channels.${channelId}.${accountId}.groupPolicy`);
      }
    }
  }
  if (process.platform !== "win32") {
    for (const directory of [paths.stateDir, paths.credentialsDir, paths.agentsDir, paths.logsDir, paths.pluginsDir]) {
      try { await fs.chmod(directory, 0o700); changed.push(`chmod 700 ${directory}`); }
      catch (error) { if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error; }
    }
    for (const file of [paths.configPath, paths.masterKeyPath]) {
      try { await fs.chmod(file, 0o600); changed.push(`chmod 600 ${file}`); }
      catch (error) { if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error; }
    }
    const credentialEntries = await fs.readdir(paths.credentialsDir, { withFileTypes: true }).catch(() => []);
    for (const entry of credentialEntries) {
      if (!entry.isFile()) continue;
      const file = path.join(paths.credentialsDir, entry.name);
      await fs.chmod(file, 0o600);
      changed.push(`chmod 600 ${file}`);
    }
  }
  await writeConfig(paths, config);
  return { changed };
}
